/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { number, string } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Error from 'yoda-core-components/lib/assets/svg/Error.svg';
import Input from 'yoda-core-components/lib/components/Input/Input';
import { triggerFormError } from 'yoda-site-components/lib/actions/AnalyticsAction';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Icon from 'yoda-core-components/lib/components/Icon';
import { isMobileWidth } from 'yoda-core-components/lib/helpers/Utils/mediaQuery';
import get from 'lodash/get';
import * as styles from './CustomPriceRange.css';
import { updateUrl, getURLParameterBykey } from '../../../utils';
import { rangeIdentifier } from '../../../configurations/config';
import CheckIcon from '../../../assets/images/Tick.svg';
import { showLoader } from '../../../actions/LoaderActions';

const cx = classNames.bind(styles);

// istanbul ignore next
export const CustomPriceRange = ({ facetId, facetName }) => {
    const dispatch = useDispatch();

    const isMinEntered = useRef(false);
    const isMaxEntered = useRef(false);
    let maxInput = '';
    let minInput = '';

    const { nullResultsMessage: noProducts = '' } = useSelector((state) =>
        get(state, 'noProducts', {})
    );
    const { requestUrl = '' } = useSelector((state) => get(state, 'commonData', {}));
    let {
        preferences: {
            customMaxPriceFilterValue: maxPrice = 99999,
            customMinPriceFilterValue: minPrice = 1,
            priceRangeInputSize = 5,
        } = {},
    } = useSelector((state) => get(state, 'context', {}));
    const isDiscountRange = facetId === 44682 || facetName?.toLowerCase() === 'discount range';
    const myRef = useRef();
    const [errorMsg, setErrorMsg] = useState('');
    const [maxMinErrorMsg, setmMxMinErrorMsg] = useState('');
    const [priceError, setPriceError] = useState(false);
    const [isMaxError, setIsMaxError] = useState();
    const [isMinError, setIsMinError] = useState();

    useEffect(() => {
        // TOFP2P-3072 fill in input values if request URL indicates we have a custom price range selected
        const urlParam = facetName === 'Price Range' ? 'Nf' : 'Dr';
        const customPriceRangeUrl = getURLParameterBykey(urlParam)?.split(' ');
        if (customPriceRangeUrl.length > 1) {
            minInput.value = customPriceRangeUrl?.[1] ?? `${minPrice}`;
            maxInput.value = customPriceRangeUrl?.[2] ?? '';
        } else {
            minInput.value = `${minPrice}`;
            maxInput.value = '';
        }
    }, [requestUrl]);

    // eslint-disable-next-line camelcase
    useEffect(() => {
        const isDiscountRange = facetId === 44682 && facetName.toLowerCase() === 'discount range';

        // istanbul ignore next
        if (noProducts.length > 0) {
            // TODO: Get facet specific error message instead of global error message.
            const currentFilterApplied = isDiscountRange
                ? getURLParameterBykey('Dr')
                : getURLParameterBykey('Nf');
            const currentFacet = isDiscountRange
                ? rangeIdentifier['44682']
                : rangeIdentifier['1414'];
            const selectedRangeFilter = new RegExp(`^${currentFacet.identifier}`, 'g');

            if (!errorMsg && !priceError && selectedRangeFilter.test(currentFilterApplied)) {
                dispatch(
                    triggerFormError([
                        {
                            errorDescription: noProducts,
                        },
                    ])
                );

                setErrorMsg(noProducts);
                setPriceError(true);
            }
        } else {
            setErrorMsg('');
            setPriceError(false);
        }
    }, [facetId, facetName, noProducts]);

    const scrollToMyRef = () => {
        const { current } = myRef;
        const parentNode = current && current.parentNode;

        if (parentNode) parentNode.scrollTop = parentNode.scrollHeight;
    };

    useEffect(() => {
        if (priceError || maxMinErrorMsg.length > 0) scrollToMyRef();
    }, [priceError, maxMinErrorMsg]);

    const isValidCurrency = (currency) =>
        !!(currency.match(/^\d{0,5}(\.\d{1,2})?$/) && Number(currency) > 0);

    const onChange = () => {
        const maxVal = maxInput && parseFloat(maxInput?.value, 10.0);
        const minVal = minInput && parseFloat(minInput?.value, 10.0);
        maxPrice = isDiscountRange ? 99 : maxPrice;
        let errorDescription = '';
        let isMaxError = maxInput && !isValidCurrency(maxInput?.value);
        let isMinError = minInput && !isValidCurrency(minInput?.value);

        if (!isMaxError)
            isMaxError = isMaxEntered.current && (maxVal < minVal || maxVal > maxPrice);
        if (!isMinError)
            isMinError = isMinEntered.current && (minVal > maxVal || minVal < minPrice);

        if (isMaxError || isMinError) {
            errorDescription = `Please enter a range from ${minPrice} to ${maxPrice}.`;
            dispatch(triggerFormError([{ errorDescription }]));
        }
        setIsMaxError(isMaxError);
        setIsMinError(isMinError);
        setmMxMinErrorMsg(errorDescription);
        setPriceError(false);
    };

    const validateMinOnChange = () => {
        isMaxEntered.current = false;
        isMinEntered.current = true;
        onChange();
    };

    const validateMaxOnChange = () => {
        isMaxEntered.current = true;
        isMinEntered.current = false;
        onChange();
    };

    const validateOnKeyPress = (event) => {
        const {
            key,
            target: { value = '' },
        } = event;

        const isValidKey =
            ['.', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].filter(
                (validKey) => key === validKey
            ).length > 0;

        // decimal values more than two digits not allowed
        const isValidDecimalValue =
            value.length > 1 && value.indexOf('.') !== -1 ? value.split('.')[1].length < 2 : true;

        if (!isValidDecimalValue || !isValidKey) event.preventDefault();
    };
    const submitCustomPrice = (event) => {
        if (isMaxError || isMinError) {
            event.preventDefault();

            return;
        }

        let priceString = null;
        const isDiscountRange = facetId === 44682 || facetName?.toLowerCase() === 'discount range';
        const currentRange = isDiscountRange ? rangeIdentifier['44682'] : rangeIdentifier['1414'];
        maxPrice = isDiscountRange ? 99 : maxPrice;

        // const currentRange = rangeIdentifier[facetId];
        // Read mode information from API response - Possible values for mode 1 or 2
        const mode = getURLParameterBykey('mode', requestUrl);
        let valueChanged = false;

        /* istanbul ignore next */
        if (maxInput.value && minInput.value === '') {
            minInput.value = minPrice;
            valueChanged = true;
        }

        if (maxInput.value === '' && minInput.value) {
            maxInput.value = maxPrice;
            valueChanged = true;
        }

        const maxValue = Number(maxInput.value);
        maxInput.value = maxValue % 1 !== 0 ? maxValue.toFixed(2) : maxValue;

        const minValue = Number(minInput.value);
        minInput.value = minValue % 1 !== 0 ? minValue.toFixed(2) : minValue;

        priceString = `${currentRange.identifier} ${minInput.value} ${maxInput.value}`;

        const keyString = currentRange.key_identifier;
        const currentFacetID = isDiscountRange ? 44682 : 1414;

        if (maxInput.value && minInput.value && (!(maxMinErrorMsg.length > 0) || valueChanged)) {
            dispatch(showLoader());
            updateUrl(
                null,
                {
                    [keyString]: priceString,
                    ...(facetId === 44682 ||
                        (facetId === 1414 && { activeFacetId: currentFacetID })),
                    mode,
                    page: null,
                },
                null,
                {
                    action: 'add',
                    name: currentRange.name,
                    value: `$${minInput.value} - $${maxInput.value}`,
                }
            );
        } else {
            validateMaxOnChange();
            validateMinOnChange();
        }
    };

    const isMobileView = isMobileWidth();
    const hasError = errorMsg || priceError || maxMinErrorMsg.length > 0;

    const getErrorMessage = useCallback(() => {
        if (errorMsg || priceError) return errorMsg;
        if (maxMinErrorMsg.length > 0) return maxMinErrorMsg;
        return '';
    }, [errorMsg, priceError, maxMinErrorMsg]);

    const errorConfig = {
        icon: {
            height: '32',
            iconName: 'ydt-error-fill',
            viewBox: '4 0 32 32',
            width: '32',
            iconComponent: <Error />,
        },
        wrapper: {
            className: dt([
                'font-open-sans',
                'normal-case',
                'text-error',
                'text-small',
                'text-error-border',
                '-mt-2.5',
                'smOnly:-mt-4',
                'font-normal',
            ]),
        },
    };
    const notDiscountRangeStyles = !isDiscountRange && dt(['ml-3']);
    const discountRangeStyles = isDiscountRange && dt(['mr-4', 'pr-4', 'text-right', 'w-full']);
    const isErrorStyles =
        isMaxError || isMinError || priceError
            ? dt(['border-2', 'border-solid', 'border-error-border'])
            : dt(['bg-white', 'border-gray-40']);
    const priceRangeConfig = {
        common: {
            inputWrapperClasses: `${dt([
                'border',
                'border-solid',
                'py-[5px]',
                'smOnly:py-3.5',
                'relative',
                'rounded-lg',
                'h-8',
                'smOnly:h-12',
                'pr-[7.5px]',
                'pl-2',
                'w-28',
                'mdOnly:w-full',
            ])} ${cx('input-symbol')}`,
            input: {
                id: null,
                maxLength: '6',
                onKeyPress: validateOnKeyPress,
                size: priceRangeInputSize,
                theme: cx(notDiscountRangeStyles, discountRangeStyles),
                type: 'text',
                wrapperTheme: cx('price-input-wrapper', {
                    dollar: !isDiscountRange,
                    percentage: isDiscountRange,
                }),
            },
            iconConfig: {
                'aria-hidden': 'true',
                children: <CheckIcon />,
                focusable: 'false',
                height: '16',
                width: '16',
                style: { transform: isMobileView ? 'translate(4px)' : 'none' },
            },
        },
        button: {
            className: dt([
                '!bg-gray-70',
                'border',
                'border-gray-70',
                'border-solid',
                'font-open-sans-semibold',
                'rounded-lg',
                'text-medium',
                'w-16',
                'h-8',
                'flex',
                'flex-col',
                'justify-center',
                'items-center',
                'my-0',
                'mr-0',
                'ml-2',
                'py-2',
                'px-3',
                'text-white',
            ]),
            onClick: submitCustomPrice,
        },
        max: {
            input: {
                onChange: validateMaxOnChange,
                placeholder: 'Max',
                automationId: `${isDiscountRange ? 'discount' : 'price'}-range-max`,
                refCallBack: (ref) => {
                    maxInput = ref;
                },
                key: `${facetName} max`,
            },
        },
        min: {
            input: {
                defaultValue: `${minPrice}`,
                onChange: validateMinOnChange,
                placeholder: 'Min',
                automationId: `${isDiscountRange ? 'discount' : 'price'}-range-min`,
                refCallBack: (ref) => {
                    minInput = ref;
                },
                key: `${facetName} min`,
            },
        },
        wrapper: {
            className: dt(['flex', 'justify-between', 'mx-0', 'my-4', 'text-small', 'gap-1']),
        },
        maxInputWrapper() {
            return {
                className: [this.common.inputWrapperClasses, cx(isErrorStyles)].join(' '),
            };
        },
        minInputWrapper() {
            return {
                className: [this.common.inputWrapperClasses, cx(isErrorStyles)].join(' '),
            };
        },
    };

    return (
        <>
            <div {...priceRangeConfig.wrapper}>
                <span {...priceRangeConfig.minInputWrapper()}>
                    <Input
                        {...{
                            ...priceRangeConfig.common.input,
                            ...priceRangeConfig.min.input,
                        }}
                    />
                </span>
                <span className={dt(['self-center', 'text-center', 'w-4', 'text-base'])}>to</span>
                <span {...priceRangeConfig.maxInputWrapper()}>
                    <Input
                        theme={dt(['border-2', 'border-solid', 'border-error-border'])}
                        {...{
                            ...priceRangeConfig.common.input,
                            ...priceRangeConfig.max.input,
                        }}
                    />
                </span>
                <button
                    {...priceRangeConfig.button}
                    className={` ${cx('custom-rangeBtn')} ${dt([
                        'sm:!py-2',
                        'sm:!px-3',
                        'bg-gray-70',
                        'rounded-lg',
                        'smOnly:min-w-[48px]',
                    ])}`}
                    type="button"
                    data-automation-id="customRangeFilter"
                >
                    <Icon {...priceRangeConfig.common.iconConfig} />
                </button>
            </div>
            <div ref={myRef}>
                {hasError && (
                    <div {...errorConfig.wrapper}>
                        {/* <Icon {...errorConfig.icon}>{errorConfig.icon.iconComponent}</Icon> */}
                        {getErrorMessage()}
                    </div>
                )}
            </div>
        </>
    );
};

CustomPriceRange.propTypes = {
    facetId: number,
    facetName: string,
};

CustomPriceRange.defaultProps = {
    facetId: null,
    facetName: '',
};

export default CustomPriceRange;
